import {
	getLocalStorageItemAsString,
	setLocalStorageItem,
} from '@atlassian/search-common/local-storage';

import { type ProductKeys } from '../../constants/products';

const OAUTH_BANNER_DISMISS_KEY = 'atlassian.unified-search.oauth-banner-dismiss-counts';

export type DismissCounts = Partial<Record<ProductKeys, number>>;

/**
 * The logic was defined in this [thread](https://atlassian.slack.com/archives/C06718QD2Q5/p1727378295868049?thread_ts=1726639342.227899)
 * @returns whether the OAuth banner for the product should be shown
 */
export const shouldShowOAuthBanner = (
	product: ProductKeys,
	enforceMaxDismissals: boolean,
): boolean => {
	const dismissCounts = getLocalStorageData();
	const hasUserDismissedThisProductBefore = (dismissCounts[product] ?? 0) > 0;
	if (enforceMaxDismissals) {
		return !(hasUserDismissedThisProductBefore || hasUserDismissedEnough(dismissCounts));
	}
	return !hasUserDismissedThisProductBefore;
};

/**
 * The max number of times the user dismisses **across products**
 */
const MAX_DISMISSALS = 4;
/**
 * @returns whether the user has dismissed enough times across all products
 * so that we stop showing the OAuth banner for all products
 */
export const hasUserDismissedEnough = (dismissCounts: DismissCounts): boolean => {
	const totalDismissalsAcrossProducts = Object.values(dismissCounts).reduce(
		(prev, curr) => (prev ?? 0) + (curr ?? 0),
		0,
	);
	return (totalDismissalsAcrossProducts ?? 0) >= MAX_DISMISSALS;
};

/**
 * Increases the value of the product's dismiss field by 1
 */
export const increaseProductDismissCount = (product: ProductKeys): void => {
	const dismissCounts = getLocalStorageData();
	dismissCounts[product] = (dismissCounts[product] ?? 0) + 1;
	saveToLocalStorage(dismissCounts);
};

/**
 * @returns the local storage object that stores products and their dismiss counts
 */
const getLocalStorageData = (): DismissCounts => {
	const serializedDismissCounts = getLocalStorageItemAsString(OAUTH_BANNER_DISMISS_KEY);
	if (!serializedDismissCounts) {
		return {};
	}
	let localStorageObject;
	try {
		localStorageObject = JSON.parse(serializedDismissCounts);
	} catch {
		return {};
	}
	return localStorageObject as DismissCounts;
};

/**
 * Saves the local storage object that stores products and their dismiss counts
 */
const saveToLocalStorage = (localStorageObject: any) => {
	setLocalStorageItem(OAUTH_BANNER_DISMISS_KEY, JSON.stringify(localStorageObject));
};

// ---------------------------------------------------------------------------
// Snooze fatigue management
// Tracks per-product snooze expiry and dismissal count independently
// ---------------------------------------------------------------------------

export const OAUTH_BANNER_SNOOZE_KEY = 'atlassian.unified-search.oauth-banner-snooze';

/** Milliseconds in one day. Exported so tests can reuse the same constant. */
export const DAY_MS: number = 24 * 60 * 60 * 1000;

/**
 * Snooze durations in days for each successive dismissal in the cycle.
 * After the 4th dismissal the cycle resets: when the 90-day snooze expires
 * the banner re-appears and the next dismiss starts the cycle again from day 1.
 *
 * Index: 0 → 1 day, 1 → 1 day, 2 → 1 day, 3 → 90 days.
 */
export const SNOOZE_DAYS = [1, 1, 1, 90] as const;

export type SnoozeEntry = {
	/** Unix timestamp (ms) after which the snooze expires. */
	expiresAt: number;
	/**
	 * The number of times the user has dismissed this product in the current
	 * cycle. Incremented on every {@link snoozeProduct} call. When this value
	 * reaches {@link SNOOZE_DAYS}.length (i.e. 4) the cycle is considered
	 * complete and the next dismiss restarts the cycle from 0.
	 *
	 * Possible stored values: 1, 2, 3, 4. After the 5th dismiss the value
	 * resets to 1 (start of a new cycle).
	 */
	dismissalCount: number;
};

export type SnoozeData = Partial<Record<ProductKeys, SnoozeEntry>>;

/**
 * @returns the full snooze data object from localStorage, or an empty object
 * if absent or unparseable.
 */
const getSnoozeData = (): SnoozeData => {
	try {
		const raw = getLocalStorageItemAsString(OAUTH_BANNER_SNOOZE_KEY);
		if (!raw) {
			return {};
		}
		return JSON.parse(raw) as SnoozeData;
	} catch {
		return {};
	}
};

/**
 * Persists the snooze data object to localStorage.
 */
const saveSnoozeData = (data: SnoozeData, logException?: (error: Error) => void): void => {
	try {
		setLocalStorageItem(OAUTH_BANNER_SNOOZE_KEY, JSON.stringify(data));
	} catch (error) {
		logException?.(error as Error);
	}
};

/**
 * Returns whether the given product is currently snoozed
 * (i.e. the snooze window has not yet expired).
 */
export const isProductSnoozed = (product: ProductKeys): boolean => {
	const entry = getSnoozeData()[product];
	if (!entry) {
		return false;
	}
	return Date.now() < entry.expiresAt;
};

/**
 * Returns the dismissal count stored for the product (0–4).
 *
 * - 0 → never dismissed (no entry yet)
 * - 1, 2, 3 → user has dismissed during the current cycle and the next dismiss
 *   should pick the next snooze duration from {@link SNOOZE_DAYS}.
 * - 4 → user has completed the cycle (the last snooze written was the 90-day
 *   one); the next dismiss will reset the cycle and snooze for 1 day again.
 */
export const getProductSnoozeDismissalCount = (product: ProductKeys): number => {
	return getSnoozeData()[product]?.dismissalCount ?? 0;
};

/**
 * Records a snooze for the given product.
 *
 * - The dismiss index used to pick the snooze duration is the stored
 *   `dismissalCount` (which represents how many times the user has already
 *   dismissed this product in the current cycle).
 * - When the stored count is `SNOOZE_DAYS.length` (i.e. 4), the previous
 *   cycle has completed and we restart the cycle from index 0 — this is the
 *   "5th dismiss" case.
 * - The stored `dismissalCount` is then incremented by 1 (so after the 1st
 *   dismiss it's 1, after the 4th it's 4, after the 5th it's 1 again).
 *
 * Examples (using SNOOZE_DAYS = [1, 1, 1, 90]):
 *   1st dismiss → snooze 1 day,  stored count: 1
 *   2nd dismiss → snooze 1 day,  stored count: 2
 *   3rd dismiss → snooze 1 day,  stored count: 3
 *   4th dismiss → snooze 90 days, stored count: 4
 *   5th dismiss → snooze 1 day (cycle restart), stored count: 1
 */
export const snoozeProduct = (
	product: ProductKeys,
	logException?: (error: Error) => void,
): void => {
	const data = getSnoozeData();
	const storedCount = data[product]?.dismissalCount ?? 0;

	// If the previous cycle has completed (count === SNOOZE_DAYS.length),
	// reset to index 0 to restart the cycle on this dismiss.
	const dismissIndex = storedCount >= SNOOZE_DAYS.length ? 0 : storedCount;

	const durationDays = SNOOZE_DAYS[dismissIndex];
	const expiresAt = Date.now() + durationDays * DAY_MS;

	// The new stored count is the index we just used plus 1. This means after
	// the 4th dismiss the count is 4 (used to detect cycle completion on the
	// 5th dismiss), and after the 5th dismiss it resets to 1.
	const nextCount = dismissIndex + 1;

	data[product] = { expiresAt, dismissalCount: nextCount };
	saveSnoozeData(data, logException);
};
